import { motion } from "framer-motion";
import MaskedReveal from "@/components/site/MaskedReveal";

const ACRONYM = [
  { letter: "R", word: "Real" },
  { letter: "I", word: "Intelligent" },
  { letter: "V", word: "Valuable" },
  { letter: "H", word: "Humans" },
];

export default function AcronymReveal() {
  return (
    <section
      id="acronym"
      data-testid="acronym-section"
      className="relative py-32 md:py-56 px-6 md:px-10 bg-black text-white border-y border-white/10 overflow-hidden"
    >
      {/* Radial deep-space glow */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,#0e0e0e_0%,#050505_60%,#000_100%)]"
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex items-baseline justify-between mb-16 md:mb-24">
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/50">
            § What R.I.V.H™ stands for
          </span>
          <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-white/50">
            The acronym
          </span>
        </div>

        <MaskedReveal
          as="p"
          trigger="view"
          className="font-mono text-xs md:text-sm uppercase tracking-[0.4em] text-white/60 mb-16 md:mb-24"
          lines={["Every letter is a person."]}
        />

        <div className="grid grid-cols-1 divide-y divide-white/10">
          {ACRONYM.map((row, i) => (
            <motion.div
              key={row.letter}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{
                duration: 1.05,
                ease: [0.22, 1, 0.36, 1],
                delay: i * 0.12,
              }}
              className="group grid grid-cols-12 items-center gap-4 md:gap-8 py-8 md:py-12 cursor-default"
              data-testid={`acronym-row-${row.letter.toLowerCase()}`}
            >
              <div className="col-span-3 md:col-span-2 font-mono text-sm md:text-base uppercase tracking-[0.35em] text-white/40 group-hover:text-white transition-colors duration-500">
                {row.letter}.
              </div>
              <div className="col-span-9 md:col-span-8 overflow-hidden">
                <motion.h3
                  className="font-display uppercase text-6xl md:text-[8.5rem] leading-[0.85] tracking-[-0.04em] text-white"
                  whileHover={{ x: 8 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  {row.word}
                </motion.h3>
              </div>
              <div className="hidden md:flex md:col-span-2 justify-end font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 group-hover:text-white transition-colors duration-500">
                {String(i + 1).padStart(2, "0")} / 04
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <p className="md:col-span-6 text-white/70 text-base md:text-lg leading-relaxed">
            R.I.V.H<sup className="text-[0.55em] align-super">™</sup> is not
            just a house of garments — it is a small orbit of{" "}
            <em className="not-italic text-white">Real</em>,{" "}
            <em className="not-italic text-white">Intelligent</em>,{" "}
            <em className="not-italic text-white">Valuable</em>{" "}
            <em className="not-italic text-white">Humans</em>. The X on your
            chest is a signal to the others in the room.
          </p>
          <p className="md:col-span-5 md:col-start-8 font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 leading-loose">
            Real — you are yourself.
            <br />
            Intelligent — you think for yourself.
            <br />
            Valuable — you build for others.
            <br />
            Humans — first, always.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
